import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";

export default function VerificationPendingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleBackToSignIn = () => {
    navigate("/signin");
  };

  return (
    <AuthLayout
      title="Check Your Email"
      subtitle="We've sent you a verification link"
      leftContent={{
        title: "Email",
        subtitle: "Verification",
        description:
          "Secure your account by verifying your email address. This ensures you can recover your account and receive important updates about your AI-powered visual commerce tools.",
        trustIndicator: "Secure & encrypted communication",
      }}
    >
      <div className="space-y-6">
        {/* Email Icon */}
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-purple-600">
            <Mail className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Email Address */}
        <div className="text-center">
          <p className="text-gray-700 mb-2">
            We've sent a verification email to:
          </p>
          <p className="font-medium text-purple-700 text-lg">{user?.email}</p>
        </div>

        {/* Instructions */}
        <div className="text-center text-gray-600 space-y-4">
          <p>
            Please check your inbox and click the verification link to activate
            your account. If you don't see the email, please check your spam
            folder.
          </p>
        </div>

        {/* Resend Verification Section */}
        <div className="text-center space-y-4">
          <div className="text-gray-600">
            <p className="text-sm mb-3">Didn't receive the email?</p>
            <Link
              to="/resend-verification"
              className="inline-flex items-center justify-center h-10 px-6 border border-purple-200 hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition-colors"
            >
              Resend Verification Email
            </Link>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-purple-700 mb-1">
                What happens next?
              </h3>
              <p className="text-sm text-gray-600">
                After verifying your email, you can sign in and check the status
                of your application. We’ll email you when it is approved.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Sign In Button */}
        <Button
          onClick={handleBackToSignIn}
          className="w-full h-12 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sign In
        </Button>
      </div>
    </AuthLayout>
  );
}
